import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { ButtonLoader, LoadingComponent, TiptapTextEditor } from "Components";
import { HtmlPreviewMultiDevice } from "Components/Modal";
import { newAuthService } from "Service/Axios";
import {
  createOfferLetterUrl,
  GET_OPERATOR_LIST_BY_PROJECT_QUESTIONS_URL,
  GET_QUESTION_LIST_BY_PROJECT_URL,
  GET_VALUES_LIST_BY_PROJECT_QUESTIONS_URL,
  getOfferLetterDetailsUrl,
  updateOfferLetterUrl,
} from "Constant/api";
import { Validator } from "Utils/validator";
import { validateTextEdiorContent } from "Utils";
import { paths } from "Constant/paths";
import { useProjectList } from "Constant/apiHooks";

export const NewOfferLetter = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { gid } = useParams();

  const defaultEventData = {
    offerLetterName: "",
    category: "",
    projectId: "",
    offerLetterContent: "",
  };

  const [loading, setLoading] = useState(false);
  const [questionDataLoading, setQuestionDataLoading] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [previewOfferLetterModal, setPreviewOfferLetterModal] = useState(false);
  const [questionsData, setQuestionData] = useState(null);
  const [questionList, setQuestionList] = useState([]);
  const [offerLetterContentDocuments, setOfferLetterContentDocuments] =
    useState([]);
  const [formData, setFormData] = useState(defaultEventData);
  const [errMsg, setErrMsg] = useState({
    offerLetterName: "",
    category: "",
    projectId: "",
    offerLetterContent: "",
  });

  const { offerLetterName, category, projectId, offerLetterContent } = formData;

  const { projectList, loading: projectListLoading } = useProjectList();

  const modifiedProjectList = useMemo(() => {
    return projectList?.map((project) => ({
      label: project.name,
      value: project.gid,
    }));
  }, [projectList]);

  // Toggle
  const togglePreviewOfferLetterModal = () => {
    setPreviewOfferLetterModal(!previewOfferLetterModal);
  };

  // On Change
  const handleChange = (e) => {
    if (e.target.type === "checkbox") {
      setFormData((prev) => ({
        ...prev,
        [e.target.name]: e.target.checked,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [e.target.name]: e.target.value,
      }));
    }

    if (e.target.name === "offerLetterName") {
      if (!Validator.text(e.target.value)) {
        setErrMsg((prev) => ({
          ...prev,
          offerLetterName: t(
            "common.messages.error.offerLetter.offerLetterName.blank"
          ),
        }));
      } else {
        setErrMsg((prev) => ({
          ...prev,
          offerLetterName: "",
        }));
      }
    }

    if (e.target.name === "category") {
      if (!Validator.text(e.target.value)) {
        setErrMsg((prev) => ({
          ...prev,
          category: t("common.messages.error.offerLetter.category.blank"),
        }));
      } else {
        setErrMsg((prev) => ({
          ...prev,
          category: "",
        }));
      }
    }

    if (e.target.name === "projectId") {
      getQuestionList(e.target.value);
      if (!Validator.text(e.target.value)) {
        setErrMsg((prev) => ({
          ...prev,
          projectId: t("common.messages.error.email.rules.projectId.blank"),
        }));
      } else {
        setErrMsg((prev) => ({
          ...prev,
          projectId: "",
        }));
      }
    }
  };

  const handleChangeOfferLetterContent = (value) => {
    const newValue = validateTextEdiorContent(value);
    setFormData((prev) => ({
      ...prev,
      offerLetterContent: newValue,
    }));
    if (!Validator.text(newValue)) {
      setErrMsg((prev) => ({
        ...prev,
        offerLetterContent: t(
          "common.messages.error.offerLetter.offerLetterContent.blank"
        ),
      }));
    } else {
      setErrMsg((prev) => ({
        ...prev,
        offerLetterContent: "",
      }));
    }
  };

  const handleChangeOfferLetterContentDocuments = (newDocument) => {
    setOfferLetterContentDocuments((prev) => [...prev, { ...newDocument }]);
  };

  const handleRemoveOfferLetterContentDocument = (gid) => {
    const updatedDocuments = offerLetterContentDocuments.filter(
      (doc) => doc.gid !== gid
    );
    setOfferLetterContentDocuments(updatedDocuments);
  };

  // Fetch Details
  const getOfferLetterDetails = async () => {
    setDetailsLoading(true);
    const payload = { gid };
    const response = await newAuthService(
      getOfferLetterDetailsUrl,
      "POST",
      payload
    );
    if (response.status) {
      setFormData({
        offerLetterName: response.data.name,
        category: response.data.category,
        offerLetterContent: response.data.jsondata,
        projectId: response.data.project?.gid || "",
      });
      setOfferLetterContentDocuments(response.data.attchemnts || []);
      setDetailsLoading(false);
    } else {
      toast.error(t("common.messages.notification.failed.serverError"));
      setDetailsLoading(false);
    }
  };

  useEffect(() => {
    if (gid) {
      getOfferLetterDetails();
    }
  }, [gid]);

  const getQuestionList = async (projectId) => {
    try {
      setQuestionDataLoading(true);
      const payload = {
        gid: projectId,
      };
      const response = await newAuthService(
        GET_QUESTION_LIST_BY_PROJECT_URL,
        "POST",
        payload
      );
      if (response.status) {
        const questionList = response.data.map((question) => ({
          label: question.name,
          value: question.questionId,
        }));
        setQuestionList(questionList);
      } else {
        setQuestionList([]);
      }
    } catch (e) {
      toast.error(e);
    } finally {
      setQuestionDataLoading(false);
    }
  };

  const getQuestionData = async (questionId, conditionId) => {
    setQuestionDataLoading(true);
    const payload = {
      questionId,
      projectId,
    };
    if (questionId && projectId) {
      const operatorResponse = await newAuthService(
        GET_OPERATOR_LIST_BY_PROJECT_QUESTIONS_URL,
        "POST",
        payload
      );

      const valuesResponse = await newAuthService(
        GET_VALUES_LIST_BY_PROJECT_QUESTIONS_URL,
        "POST",
        payload
      );
      if (operatorResponse.status && valuesResponse.status) {
        const updatedOperators = operatorResponse.data.map((op) => ({
          label: op.name,
          value: op.name,
        }));
        const newObject = {
          [conditionId]: {
            values: valuesResponse.data,
            operators: updatedOperators,
          },
        };
        setQuestionData((prevQuestionData) => ({
          ...prevQuestionData,
          ...newObject,
        }));
      } else {
        const newObject = {
          [conditionId]: {
            values: null,
            operators: [],
          },
        };
        setQuestionData((prevQuestionData) => ({
          ...prevQuestionData,
          ...newObject,
        }));
      }
    }
    setQuestionDataLoading(false);
  };

  // Validation
  const getValidation = () => {
    return new Promise((resolve) => {
      if (
        Validator.text(offerLetterName) &&
        Validator.text(category) &&
        Validator.text(offerLetterContent)
      ) {
        resolve(true);
      } else {
        if (!Validator.text(offerLetterName)) {
          setErrMsg((prev) => ({
            ...prev,
            offerLetterName: t(
              "common.messages.error.offerLetter.offerLetterName.blank"
            ),
          }));
        }

        if (!Validator.text(category)) {
          setErrMsg((prev) => ({
            ...prev,
            category: t("common.messages.error.offerLetter.category.blank"),
          }));
        }

        if (!Validator.text(offerLetterContent)) {
          setErrMsg((prev) => ({
            ...prev,
            offerLetterContent: t(
              "common.messages.error.offerLetter.offerLetterContent.blank"
            ),
          }));
        }

        resolve(false);
      }
    });
  };

  // Submit & Reset
  const onSubmit = async () => {
    const validationStatus = await getValidation();
    if (validationStatus) {
      setLoading(true);
      try {
        const selectedProject = modifiedProjectList.find(
          (project) => project.value === projectId
        );
        const payload = {
          name: offerLetterName,
          category,
          jsondata: offerLetterContent,
          html: `<!DOCTYPE html><html><head> <title>${offerLetterName}</title> <style> </style></head><body>${offerLetterContent}</body></html>`,
          project: {
            gid: selectedProject ? selectedProject.value : "",
            name: selectedProject ? selectedProject.label : "",
          },
          attchemnts: offerLetterContentDocuments,
        };
        if (gid) {
          payload.gid = gid;
        }
        const response = await newAuthService(
          gid ? updateOfferLetterUrl : createOfferLetterUrl,
          "POST",
          payload
        );
        if (response.status) {
          toast.success(
            t(
              `common.messages.notification.success.${
                gid ? "updateOfferLetter" : "createOfferLetter"
              }`
            )
          );
          navigate(paths.admin.offerLetter.root);
          setLoading(false);
        } else {
          toast.error(response.message);
          setLoading(false);
        }
      } catch (error) {
        toast.error(
          t("common.messages.notification.failed.somethingWentWrong")
        );
        setLoading(false);
      }
    }
  };

  const onResetQuestionData = () => {
    setQuestionData(null);
  };

  const onRedirect = () => {
    navigate(paths.admin.offerLetter.root);
  };

  return (
    <main className="main">
      <div className="mb-4">
        <div className="p-3 rounded-3 bg-white position-relative">
          <div className="d-flex flex-wrap align-items-center row-gap-3 mb-3">
            <div className="d-flex align-items-center column-gap-2">
              <button
                className="border-0 bg-transparent flex-auto w-10 h-10 d-flex justify-content-center align-items-center bg-slate-100 rounded-circle"
                onClick={onRedirect}
              >
                <i className="bx bx-arrow-back"></i>
              </button>
              <h3 className="mb-0 text-md font-semibold text-slate-700">
                {gid ? "Edit" : "Create"} Offer Letter
              </h3>
            </div>
          </div>

          <LoadingComponent isLoading={detailsLoading || projectListLoading}>
            <div className="row">
              <div className="col-lg-6 col-md-12 col-sm-12">
                <div className="mb-3">
                  <label htmlFor="offerLetterName" className="form-label">
                    Offer Letter Name
                  </label>
                  <input
                    className="form-control"
                    type="text"
                    placeholder=""
                    id="offerLetterName"
                    name="offerLetterName"
                    value={offerLetterName}
                    onChange={handleChange}
                  />
                  {errMsg.offerLetterName && (
                    <p className="error">{errMsg.offerLetterName}</p>
                  )}
                </div>
              </div>
              <div className="col-lg-6 col-md-12 col-sm-12">
                <div className="mb-3">
                  <label htmlFor="category" className="form-label">
                    Offer Letter Category
                  </label>
                  <input
                    className="form-control"
                    type="text"
                    placeholder=""
                    id="category"
                    name="category"
                    value={category}
                    onChange={handleChange}
                  />
                  {errMsg.category && (
                    <p className="error">{errMsg.category}</p>
                  )}
                </div>
              </div>
              <div className="col-lg-12 col-md-12 col-sm-12">
                <div className="mb-4">
                  <label>{t("common.label.chooseProject")}</label>
                  <select
                    className="form-select form-select-md"
                    name="projectId"
                    value={projectId}
                    onChange={handleChange}
                  >
                    <option>{t("common.label.select")}</option>
                    {modifiedProjectList &&
                      modifiedProjectList.length > 0 &&
                      modifiedProjectList.map((prjct, idx) => (
                        <option
                          key={`${prjct.value}-${idx}`}
                          value={prjct.value}
                        >
                          {prjct.label}
                        </option>
                      ))}
                  </select>
                  {errMsg.projectId && (
                    <p className="error">{errMsg.projectId}</p>
                  )}
                </div>
              </div>
              <div className="col-lg-12 col-md-12 col-sm-12">
                <div className="mb-3 TiptapTextEditor-box">
                  <label htmlFor="offerLetterContent" className="form-label">
                    Offer Letter Content
                  </label>
                  <TiptapTextEditor
                    value={offerLetterContent}
                    handleChange={handleChangeOfferLetterContent}
                    documentList={offerLetterContentDocuments}
                    handleDocumentChange={
                      handleChangeOfferLetterContentDocuments
                    }
                    handleDocumentRemove={
                      handleRemoveOfferLetterContentDocument
                    }
                    showCondBlock={true}
                    isDisabledCondBlock={!projectId}
                    condBlockVarList={questionList}
                    condBlockVarListLoading={questionDataLoading}
                    condBlockData={questionsData}
                    condBlockDataLoading={questionDataLoading}
                    resetCondBlockData={onResetQuestionData}
                    showVariableDropdown={true}
                    variableList={questionList.map(
                      (question) => question.label
                    )}
                    fetchOperatorsAndValue={getQuestionData}
                  />
                  {errMsg.offerLetterContent && (
                    <p className="error">{errMsg.offerLetterContent}</p>
                  )}
                </div>
              </div>
            </div>

            <div className="row align-items-center">
              <div className="col-auto ms-md-auto">
                <ul className="email-btn-list list-unstyled d-flex gap-2">
                  <li>
                    <button
                      type="button"
                      className="btn btn-transparent"
                      onClick={onRedirect}
                    >
                      {t("common.button.cancel")}
                    </button>
                  </li>
                  <li>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={togglePreviewOfferLetterModal}
                    >
                      {t("common.button.preview")}
                    </button>
                  </li>
                  <li>
                    {!loading ? (
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={onSubmit}
                      >
                        {t("common.button.save")}
                      </button>
                    ) : (
                      <ButtonLoader className="btn-loading-sm" />
                    )}
                  </li>
                </ul>
              </div>
            </div>
          </LoadingComponent>
        </div>
      </div>

      {previewOfferLetterModal && (
        <HtmlPreviewMultiDevice
          show={previewOfferLetterModal}
          htmlData={offerLetterContent}
          toggle={togglePreviewOfferLetterModal}
        />
      )}
    </main>
  );
};
